import { z } from 'zod';
import { gameInstanceIndexSchema } from '../types/gameInstance';

/**
 * Android multi-instance wire schemas (host ↔ Playjector).
 *
 * Unlike the other files in this folder — which validate the TS
 * `PlaycastMessage` envelope (`header` + `body.message`) — these zod schemas
 * mirror the C++ wire shape from
 * `apps/playjector/src/PlayJectorLib/source/SchemaList.cpp` verbatim:
 * an `address` object (`target`/`action`/`isReply`) plus flat top-level
 * fields. Their job is C++↔TS contract parity, exercised by
 * `processInstances.contract.test.ts` against the
 * docs/feature-plans/android-multi-instance-v2/PHASE_1.md examples. The
 * envelope-level TS types live in `types/messages/processInstances.ts`.
 *
 * All schemas are `.strict()` — unknown fields are rejected, matching the
 * C++ schema-loader semantics (the android launch package in particular must
 * reject monitor/display/vmId fields; the machine owns that lifecycle).
 *
 * NOTE — doc drift: the v2 doc's JSONC block shows the capabilities query
 * with action "capabilities"; the implemented C++ schema uses action
 * "capabilitiesQuery" for the query (isReply false) and "capabilities" for
 * the reply (isReply true) so each message keys to its own routing name.
 * These schemas mirror the implemented wire.
 */

const processAddressSchema = <A extends string, R extends boolean>(action: A, isReply: R) =>
  z
    .object({
      target: z.literal('process'),
      action: z.literal(action),
      isReply: z.literal(isReply),
    })
    .strict();

/** processCapabilitiesQueryMSG (host → playjector). */
export const processCapabilitiesQuerySchema = z
  .object({
    address: processAddressSchema('capabilitiesQuery', false),
  })
  .strict();

/**
 * processCapabilitiesReplyMSG (playjector → host).
 * `provisionedInstanceCount` = pre-created VMs found; `maxGameInstances` =
 * ceiling the host must clamp `instanceCount` to. Both may be 0 (machine
 * reports no multi-instance capability), so non-negative is the constraint.
 */
export const processCapabilitiesReplySchema = z
  .object({
    address: processAddressSchema('capabilities', true),
    provisionedInstanceCount: z.number().int().nonnegative(),
    maxGameInstances: z.number().int().nonnegative(),
  })
  .strict();

/**
 * processInstanceLaunchedReplyMSG (playjector → host, per instance).
 * The `shadowCamName` echo is the truth for the instance → camera mapping.
 * isReply is true so RemoteIO broadcasts it to the socket clients.
 */
export const processInstanceLaunchedSchema = z
  .object({
    address: processAddressSchema('instanceLaunched', true),
    streamId: z.string(),
    gameInstanceIndex: gameInstanceIndexSchema,
    shadowCamName: z.string(),
    result: z.string(),
    error: z.string().optional(),
  })
  .strict();

/** processInstanceClosedReplyMSG (playjector → host): one instance closed/died. */
export const processInstanceClosedSchema = z
  .object({
    address: processAddressSchema('instanceClosed', true),
    streamId: z.string(),
    gameInstanceIndex: gameInstanceIndexSchema,
    reason: z.string(),
  })
  .strict();

/** processRestartInstanceMSG (host → playjector): restart one instance. */
export const processRestartInstanceSchema = z
  .object({
    address: processAddressSchema('restartInstance', false),
    streamId: z.string(),
    gameInstanceIndex: gameInstanceIndexSchema,
  })
  .strict();

export const processRestartInstanceReplySchema = z
  .object({
    address: processAddressSchema('restartInstance', true),
    streamId: z.string(),
    gameInstanceIndex: gameInstanceIndexSchema,
    result: z.string(),
  })
  .strict();

/**
 * androidLaunchPackage — shape of `processLaunchMSG.launchPackage` for the
 * android launcher. Post-`48c44a42b1` the C++ `ParseAndroidLaunchRequest`
 * reads package/streamId/instanceCount/readyWaitTimeMs only — `apkSource`
 * is GONE from the contract (VMs are pre-warmed by provisioning; the
 * transitional fallback self-warm was removed). Strict: `apkSource` and
 * monitor/display/vmId fields are all rejected — the host omits them and
 * the machine owns that lifecycle.
 */
export const androidLaunchPackageSchema = z
  .object({
    launcher: z.literal('android'),
    package: z.string(),
    streamId: z.string(),
    instanceCount: z.number().int().positive(),
    readyWaitTimeMs: z.number().int().nonnegative(),
  })
  .strict();

/**
 * Input-envelope instance routing (touch/mouse/keyboard/gamepad messages,
 * e.g. C++ `touchDownMSG`): `streamId` plus an optional `gameInstanceIndex`.
 * Absent = 0 = single-instance streams; existing senders unaffected.
 */
export const inputEnvelopeInstanceRoutingSchema = z
  .object({
    streamId: z.string(),
    gameInstanceIndex: gameInstanceIndexSchema.optional(),
  })
  .strict();

export type ProcessCapabilitiesReplyWire = z.infer<typeof processCapabilitiesReplySchema>;
export type ProcessInstanceLaunchedWire = z.infer<typeof processInstanceLaunchedSchema>;
export type ProcessInstanceClosedWire = z.infer<typeof processInstanceClosedSchema>;
export type ProcessRestartInstanceWire = z.infer<typeof processRestartInstanceSchema>;
export type ProcessRestartInstanceReplyWire = z.infer<typeof processRestartInstanceReplySchema>;
export type AndroidLaunchPackageWire = z.infer<typeof androidLaunchPackageSchema>;
